/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { ExportOutlined } from '@ant-design/icons'
import type { ICellRendererParams } from 'ag-grid-community'
import { Tooltip } from 'antd'
import { Link } from 'react-router-dom'

import { ROUTES } from '@sportninja/common/constants/app'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'

import type { GameGridRow } from '../utils/gameGridRows'
import { gameViewsLabel } from '../utils/i18n'

/**
 * The grid's way into a single game.
 *
 * It has to be its OWN column. Every other cell is either editable — where a
 * click focuses and a double-click opens an editor — or part of the selection
 * gesture, so there is no cell left that could take a navigation click without
 * spending one of those, and a row-level click would fight both.
 *
 * It opens in a NEW TAB. This grid is a working surface: an admin has scrolled
 * to a spot, loaded pages behind them and often has rows selected, and
 * navigating in place throws all of that away for a look at one game. A real
 * `href` — not an `onClick` — is what makes ⌘-click, middle-click and "open in
 * new tab" behave too.
 */
const GameLinkCell = ({ data }: ICellRendererParams<GameGridRow>) => {
  'use memo'

  //  AG Grid renders this cell for its loading and full-width rows with no data.
  if (data === undefined) {
    return null
  }

  //  Title-cased at the render site, not in the locale file: the convention
  //  keeps short action labels consistent even if a translation drifts.
  const label = capitalizeWords(gameViewsLabel('gameGridOpenGame'))

  return (
    <Tooltip title={label} mouseEnterDelay={0.3}>
      <Link
        to={`${ROUTES.GAME_ROOT}/${data.id}`}
        target='_blank'
        //  `noopener` is the security half (the new tab cannot reach back
        //  through `window.opener`); `noreferrer` keeps it from leaking the
        //  admin's current URL.
        rel='noopener noreferrer'
        css={linkStyles}
        aria-label={label}
        data-testid={`game-open-link-${data.id}`}
      >
        <ExportOutlined aria-hidden />
      </Link>
    </Tooltip>
  )
}

// ─── Styles ──────────────────────────────────────────────────────

const linkStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: ${colors.NEUTRAL_50};
  font-size: 15px;
  transition: color 0.15s ease;

  &:hover,
  &:focus-visible {
    color: ${colors.PRIMARY};
  }
`

export default GameLinkCell
